@if (ideas && !failed) {
<section class="mb-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white shadow-sm" aria-label="Ideas for this hour">

  <!-- One-line header: always visible, folds the panel -->
  <div class="flex items-center gap-3 px-4 py-2.5 cursor-pointer select-none" (click)="toggle()">
    <span class="h-8 w-8 rounded-lg bg-amber-400 flex items-center justify-center flex-shrink-0">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] text-neutral-900">lightbulb</mat-icon>
    </span>
    <div class="min-w-0 flex-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <span class="text-sm font-bold text-gray-900">Ideas for this hour</span>
        <span class="text-xs text-gray-500">updated {{ ideas.hour }} · next at {{ ideas.nextUpdate }}</span>
      </div>
      @if (collapsed && summary) { <div class="text-xs text-gray-600 truncate">{{ summary }}</div> }
    </div>
    <button type="button" class="h-8 w-8 rounded-lg text-gray-400 hover:bg-white hover:text-gray-700 flex items-center justify-center"
      (click)="$event.stopPropagation(); load()" [disabled]="loading" matTooltip="Refresh now" aria-label="Refresh ideas">
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px]" [class.animate-spin]="loading">refresh</mat-icon>
    </button>
    <button type="button" class="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-white hover:text-gray-900"
      [attr.aria-expanded]="!collapsed">
      {{ collapsed ? 'Show' : 'Hide' }}
      <mat-icon class="!text-[18px] !h-[18px] !w-[18px] transition-transform" [class.rotate-180]="!collapsed">expand_more</mat-icon>
    </button>
  </div>

  @if (!collapsed) {
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-3 px-4 pb-4">

    <!-- Call next -->
    <div class="bg-white rounded-xl border border-gray-100 p-3">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
        <mat-icon class="!text-[16px] !h-4 !w-4 text-emerald-600">call</mat-icon> Call next
      </h3>
      @if (!ideas.callNext.length) {
        <p class="text-sm text-gray-500 py-3">Nobody waiting for a call right now.</p>
      }
      <ul class="flex flex-col gap-2">
        @for (c of ideas.callNext; track c.kind + c.id) {
        <li class="rounded-lg border border-gray-100 px-3 py-2 hover:border-amber-200 transition-colors">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-semibold text-gray-900 truncate">{{ c.name }}</span>
                <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 whitespace-nowrap flex items-center gap-0.5">
                  <mat-icon class="!text-[12px] !h-3 !w-3">{{ leadIcon(c.kind) }}</mat-icon>{{ leadLabel(c.kind) }}
                </span>
              </div>
              <p class="text-xs text-gray-600 mt-0.5">{{ c.reason }}</p>
            </div>
          </div>
          @if (c.nearby) {
          <div class="mt-1.5 rounded-md bg-violet-50 px-2 py-1 text-xs text-violet-900 flex items-center gap-1.5"
            [matTooltip]="c.nearby.views + ' views from ' + c.nearby.county + ' in the last week'">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5 flex-shrink-0">location_on</mat-icon>
            <span class="truncate">Popular near them ({{ c.nearby.county }}): <strong>{{ c.nearby.productName }}</strong></span>
            <button type="button" class="ml-auto flex-shrink-0 font-semibold underline hover:no-underline" (click)="startSale(c.nearby.productSlug)">Open</button>
          </div>
          }
          <div class="flex items-center gap-1.5 mt-1.5">
            @if (c.phone) {
            <a [href]="telLink(c.phone)" class="flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
              <mat-icon class="!text-[14px] !h-3.5 !w-3.5">call</mat-icon> {{ c.phone }}
            </a>
            }
            <button type="button" class="px-2.5 py-1 rounded-md border border-gray-200 text-xs font-semibold text-gray-700 hover:border-amber-300 hover:bg-amber-50"
              (click)="openLead(c.kind)" matTooltip="Open the list to log the call or start their sale">Open</button>
          </div>
        </li>
        }
      </ul>
    </div>

    <!-- Pitch this hour -->
    <div class="bg-white rounded-xl border border-gray-100 p-3">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
        <mat-icon class="!text-[16px] !h-4 !w-4 text-amber-600">campaign</mat-icon> Pitch this hour
      </h3>
      @if (!ideas.pitch.length) {
        <p class="text-sm text-gray-500 py-3">No product suggestions this hour.</p>
      }
      <ul class="flex flex-col gap-2">
        @for (p of ideas.pitch; track p.productId) {
        <li class="flex items-center gap-2.5 rounded-lg border border-gray-100 px-2.5 py-2 hover:border-amber-200 transition-colors">
          @if (p.image) { <img [src]="p.image" alt="" class="h-10 w-10 rounded-md object-cover bg-gray-100 flex-shrink-0" loading="lazy" /> }
          @else { <span class="h-10 w-10 rounded-md bg-gray-100 flex-shrink-0"></span> }
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-gray-900 truncate" [title]="p.productName">{{ p.productName }}</div>
            <div class="text-xs text-gray-600 flex items-start gap-1">
              <mat-icon class="!text-[13px] !h-3.5 !w-3.5 text-amber-600 flex-shrink-0 mt-px">{{ pitchIcon(p.kind) }}</mat-icon>
              <span>{{ p.reason }}</span>
            </div>
          </div>
          <div class="flex flex-col items-end gap-1 flex-shrink-0">
            <span class="text-xs text-gray-500">from £{{ p.from | number:'1.2-2' }}</span>
            <button type="button" class="px-2.5 py-1 rounded-md bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 text-xs font-bold hover:from-amber-300 hover:to-amber-400"
              (click)="startSale(p.productSlug)" matTooltip="Open a new sale with this product">Start sale</button>
          </div>
        </li>
        }
      </ul>
    </div>

    <!-- This hour -->
    <div class="bg-white rounded-xl border border-gray-100 p-3 flex flex-col gap-3">
      <h3 class="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
        <mat-icon class="!text-[16px] !h-4 !w-4 text-sky-600">schedule</mat-icon> Today so far
      </h3>
      @if (ideas.pace) {
      <div>
        <div class="flex items-baseline justify-between gap-2">
          <span class="text-2xl font-bold text-gray-900 tabular-nums">{{ ideas.pace.orders }} <span class="text-sm font-semibold text-gray-500">order{{ ideas.pace.orders === 1 ? '' : 's' }}</span></span>
          <span class="text-sm font-semibold text-gray-900 tabular-nums">£{{ ideas.pace.revenue | number:'1.0-0' }}</span>
        </div>
        <div class="h-2 rounded-full bg-gray-100 mt-1.5" [matTooltip]="'Usually ' + ideas.pace.usualOrders + ' orders by this time on a ' + ideas.pace.weekday">
          <div class="h-2 rounded-full" [class]="paceAhead ? 'bg-emerald-500' : 'bg-amber-500'" [style.width.%]="pacePct"></div>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          A usual {{ ideas.pace.weekday }} has {{ ideas.pace.usualOrders }} orders (£{{ ideas.pace.usualRevenue | number:'1.0-0' }}) by now{{ paceAhead ? ' - ahead of a usual day.' : '.' }}
        </p>
      </div>
      }
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center justify-between gap-2">
          <span class="flex items-center gap-1.5"><mat-icon class="!text-[16px] !h-4 !w-4 text-sky-600">donut_small</mat-icon> Placed today by status</span>
          @if (todayOrderCount) {
          <button type="button" class="normal-case tracking-normal font-semibold text-amber-700 hover:underline" (click)="openOrders.emit('ALL')">See all {{ todayOrderCount }}</button>
          }
        </div>
        @if (ideas.statusCounts?.length) {
        <div class="grid grid-cols-2 gap-1.5">
          @for (t of ideas.statusCounts; track t.status) {
          <button type="button" class="flex items-center gap-2 rounded-lg px-2 py-1.5 bg-gray-50 border border-transparent text-left hover:bg-white hover:border-gray-200 hover:shadow-sm transition-all"
            (click)="openOrders.emit(t.status)" [matTooltip]="'£' + (t.value | number:'1.0-0') + ' - click to see these orders'">
            <span class="h-2 w-2 rounded-full flex-shrink-0" [class]="statusTone(t.status).dot"></span>
            <span class="text-xs text-gray-700 truncate flex-1">{{ statusLabel(t.status) }}</span>
            <span class="text-sm font-bold text-gray-900 tabular-nums">{{ t.count }}</span>
          </button>
          }
        </div>
        } @else {
        <p class="text-xs text-gray-500">No orders placed yet today.</p>
        }
      </div>
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
          <mat-icon class="!text-[16px] !h-4 !w-4 text-amber-500">emoji_events</mat-icon> Top agents today
        </div>
        @if (ideas.topAgents?.length) {
        <ol class="flex flex-col gap-1">
          @for (a of ideas.topAgents; track a.agentId; let i = $index) {
          <li class="flex items-center gap-2 rounded-lg px-2 py-1.5" [class]="i === 0 ? 'bg-amber-50 border border-amber-100' : 'bg-gray-50'">
            <span class="h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
              [class]="i === 0 ? 'bg-amber-400 text-neutral-900' : i === 1 ? 'bg-gray-300 text-gray-800' : i === 2 ? 'bg-orange-200 text-orange-900' : 'bg-gray-100 text-gray-500'">{{ i + 1 }}</span>
            <span class="text-sm font-semibold text-gray-900 truncate flex-1">{{ a.name }}</span>
            <span class="text-xs text-gray-500 whitespace-nowrap tabular-nums">{{ a.orders }} order{{ a.orders === 1 ? '' : 's' }}</span>
            <span class="text-sm font-bold text-gray-900 whitespace-nowrap tabular-nums w-20 text-right">£{{ a.revenue | number:'1.0-0' }}</span>
          </li>
          }
        </ol>
        } @else {
        <p class="text-xs text-gray-500">No agent sales yet today - first one takes the top spot.</p>
        }
      </div>
      @if (ideas.tip) {
      <div class="rounded-lg border px-3 py-2 text-xs flex items-start gap-2" [class]="tipClass(ideas.tip.kind)">
        <mat-icon class="!text-[16px] !h-4 !w-4 flex-shrink-0">{{ tipIcon(ideas.tip.kind) }}</mat-icon>
        <span>{{ ideas.tip.text }}</span>
      </div>
      }
      @if (ideas.hotspots.length) {
      <div>
        <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 flex items-center gap-1.5">
          <mat-icon class="!text-[16px] !h-4 !w-4 text-violet-600">location_on</mat-icon> Where interest is (since yesterday)
        </div>
        <ul class="flex flex-col gap-1">
          @for (h of ideas.hotspots; track h.county) {
          <li class="text-xs text-gray-700 flex items-baseline gap-1.5" [matTooltip]="h.region">
            <strong class="text-gray-900 whitespace-nowrap">{{ h.county }}</strong>
            <span class="text-gray-500 whitespace-nowrap tabular-nums">{{ h.views | number }} views</span>
            @if (h.topProduct) {
            <span class="truncate">· mostly <button type="button" class="underline hover:no-underline" (click)="startSale(h.topProduct.productSlug)">{{ h.topProduct.productName }}</button></span>
            }
          </li>
          }
        </ul>
        <p class="text-[11px] text-gray-500 mt-1">A caller from one of these areas? Mention what their neighbours are looking at.</p>
      </div>
      }
    </div>
  </div>
  }
</section>
}
